import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CREDIT_PACKAGES, STARTING_PRICE } from '../../config/pricing.config';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './home.html',
  styleUrls: ['./home.scss']
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  private observer!: IntersectionObserver;
  private counterObserver!: IntersectionObserver;
  private countersAnimated = false;

  isLoggedIn = false;
  demoUrl = '';
  demoOutput = '';

  // Stats data
  stats = [
    { label: 'URLs Indexed', target: 10, suffix: 'M+', current: 0 },
    { label: 'Active Users', target: 5000, suffix: '+', current: 0 },
    { label: 'Uptime', target: 99.9, suffix: '%', current: 0, decimal: true },
    { label: 'Avg Time', target: 2, suffix: 'min', current: 0, prefix: '<' }
  ];

  // Platforms
  platforms = [
    'WordPress', 'Medium', 'Shopify', 'Webflow', 'Ghost', 'Blogger', 'Tumblr', 'Reddit', 'Wix', 'Squarespace'
  ];

  // How it works
  steps = [
    {
      number: '01',
      title: 'Paste your URLs',
      description: 'Add URLs one per line, or upload a CSV/TXT file. We auto-deduplicate and validate.',
      icon: 'upload'
    },
    {
      number: '02',
      title: 'We ping the engines',
      description: 'Our distributed crawler fleet requests immediate indexation from Google, Bing, and others.',
      icon: 'radar'
    },
    {
      number: '03',
      title: 'Track results live',
      description: 'Watch indexing status update in real-time. Get notified the moment your URLs are discovered.',
      icon: 'chart'
    }
  ];

  // Features
  features = [
    {
      icon: 'zap',
      title: 'Lightning Fast',
      description: 'Most URLs indexed within 2 minutes. We deploy crawlers the moment you hit submit.'
    },
    {
      icon: 'shield',
      title: '98.5% Success Rate',
      description: 'Industry-leading index rate across all major search engines, verified by third-party audits.'
    },
    {
      icon: 'code',
      title: 'Developer API',
      description: 'REST API with simple API key auth. Submit URLs, check status, and integrate into your pipeline.'
    },
    {
      icon: 'clock',
      title: 'Drip Mode',
      description: 'Schedule submissions over hours or days to simulate natural link discovery patterns.'
    },
    {
      icon: 'database',
      title: 'Bulk Upload',
      description: 'Submit thousands of URLs at once via CSV or TXT. Smart deduping saves your credits.'
    },
    {
      icon: 'activity',
      title: 'Real-time Tracking',
      description: 'See live status for every submission. Detailed crawl logs and response codes included.'
    },
    {
      icon: 'credit',
      title: 'Pay Per Use',
      description: 'No subscriptions. Buy credits, use them anytime. Credits never expire, no lock-ins.'
    },
    {
      icon: 'lock',
      title: 'Secure by Default',
      description: 'Encrypted API keys, JWT auth, rate limiting, and SOC2-compliant infrastructure.'
    }
  ];

  // Pricing
  packages = CREDIT_PACKAGES;

  // FAQ
  homeFaqs = [
    {
      question: 'What is URL indexing and why does it matter?',
      answer: 'URL indexing is the process of getting search engines to discover and register your pages. Without indexing, your content cannot appear in search results regardless of how well-optimized it is. Our service accelerates this from weeks to minutes.',
      open: false
    },
    {
      question: 'How fast will my URLs get indexed?',
      answer: `Most URLs are processed within 2 minutes of submission. The actual indexing by search engines depends on domain authority and crawl frequency, but our crawler fleet drastically accelerates the discovery process.`,
      open: false
    },
    {
      question: 'What is your index success rate?',
      answer: 'We maintain a 98.5% index success rate across all submissions. URLs that fail are typically on domains with technical issues (blocked by robots.txt, returning 4xx/5xx errors, or marked noindex).',
      open: false
    },
    {
      question: 'How do credits work?',
      answer: `Each unique URL submission costs 1 credit. Duplicate URLs are automatically detected and won't consume additional credits. You can purchase credit packages starting from $${STARTING_PRICE}. Credits never expire.`,
      open: false
    },
    {
      question: 'Do you offer an API for developers?',
      answer: 'Yes. We provide a full REST API with API key authentication. You can programmatically submit URLs, check status, and manage your account. Code examples are available for cURL, Python, Node.js, and PHP.',
      open: false
    },
    {
      question: 'What is Drip Mode?',
      answer: 'Drip Mode spreads your URL submissions over a configurable time window instead of submitting them all at once. This simulates organic discovery patterns and helps avoid triggering search engine spam filters.',
      open: false
    },
    {
      question: 'Can I upload URLs in bulk?',
      answer: 'Absolutely. You can paste multiple URLs directly, or upload CSV and TXT files containing thousands of URLs. Our deduplication engine ensures you never waste credits on repeated submissions.',
      open: false
    },
    {
      question: 'Is there a refund policy?',
      answer: 'Yes. If you are unsatisfied with the service, we offer a full refund on unused credits. Contact our support team at support@quickindexingtool.com for assistance.',
      open: false
    }
  ];

  // Testimonials (anonymized)
  testimonials = [
    { name: 'Alex M.', role: 'SEO Agency Owner', text: 'Cut our indexation time from weeks to hours. The API is rock solid and the drip feature is a game changer.' },
    { name: 'Sarah K.', role: 'Affiliate Marketer', text: 'We index 500+ URLs daily across multiple sites. Quick Indexing handles it without breaking a sweat.' },
    { name: 'David L.', role: 'SaaS Founder', text: 'Clean API, transparent pricing, real-time tracking. Exactly what a technical SEO tool should be.' }
  ];

  constructor(private router: Router) {}

  ngOnInit() {
    this.checkAuthStatus();
  }

  ngAfterViewInit() {
    this.initScrollAnimations();
    this.initCounterAnimation();
  }

  ngOnDestroy() {
    if (this.observer) this.observer.disconnect();
    if (this.counterObserver) this.counterObserver.disconnect();
  }

  private checkAuthStatus() {
    const token = localStorage.getItem('auth_token');
    this.isLoggedIn = !!token;
  }

  getStarted() {
    if (this.isLoggedIn) {
      this.router.navigate(['/dashboard']);
    } else {
      this.router.navigate(['/login']);
    }
  }

  runDemo() {
    if (!this.demoUrl || !this.demoUrl.startsWith('http')) {
      this.demoOutput = '✗ Please enter a valid URL starting with http:// or https://';
      return;
    }
    this.demoOutput = '> Analyzing URL...';
    setTimeout(() => {
      this.demoOutput = `> Submitting to crawler fleet...
> Pinging Google Indexing API...
> Pinging Bing Webmaster API...
> Pinging 47 web 2.0 nodes...

✓ Queued for indexing
✓ Estimated index time: 1-2 minutes
✓ 1 credit will be used on submit`;
    }, 800);
  }

  scrollToFeatures() {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  }

  toggleHomeFaq(faq: any) {
    faq.open = !faq.open;
  }

  getDiscount(pkg: any): number {
    return Math.round(((pkg.original_price - pkg.price) / pkg.original_price) * 100);
  }

  getPerCredit(pkg: any): string {
    return (pkg.price / pkg.credits).toFixed(2);
  }

  private initScrollAnimations() {
    if (typeof IntersectionObserver === 'undefined') return;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          this.observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    setTimeout(() => {
      const elements = document.querySelectorAll('.reveal');
      elements.forEach(el => this.observer.observe(el));
    }, 100);
  }

  private initCounterAnimation() {
    if (typeof IntersectionObserver === 'undefined') return;

    this.counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.countersAnimated) {
          this.countersAnimated = true;
          this.animateCounters();
          this.counterObserver.disconnect();
        }
      });
    }, { threshold: 0.3 });

    setTimeout(() => {
      const statsRibbon = document.querySelector('.stats-strip');
      if (statsRibbon) this.counterObserver.observe(statsRibbon);
    }, 100);
  }

  private animateCounters() {
    const duration = 1800;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = this.easeOutCubic(step / steps);

      this.stats.forEach(stat => {
        if (stat.decimal) {
          stat.current = parseFloat((stat.target * progress).toFixed(1));
        } else {
          stat.current = Math.round(stat.target * progress);
        }
      });

      if (step >= steps) {
        clearInterval(timer);
        this.stats.forEach(stat => stat.current = stat.target);
      }
    }, interval);
  }

  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  formatStatValue(stat: any): string {
    if (stat.decimal) {
      return (stat.prefix || '') + stat.current.toFixed(1) + stat.suffix;
    }
    if (stat.target >= 1000) {
      return (stat.prefix || '') + stat.current.toLocaleString() + stat.suffix;
    }
    return (stat.prefix || '') + stat.current + stat.suffix;
  }
}
